import React, { useState, useMemo } from 'react';
import { Box, Grid, Card, CardContent, Typography, Avatar, Chip } from '@mui/material';
import { SportsBasketball as ActivitiesIcon, Add as AddIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageHeader, EmptyState, LoadingSpinner, DataCard } from '../../components/common';
import { ActivityFormDialog, type ActivityData } from '../../components/dialogs';
import { useFamilyMembersData, useSharedPersonData, type PersonDataItem } from '../../hooks';
import { useFamily } from '../../context';
import { colors } from '../../theme';
import { updateData, deletePersonData } from '../../services/dataApi';

const ActivitiesPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { memberList, getMemberName, getMemberColor } = useFamily();

  // Get all person IDs from member list
  const personIds = useMemo(() => memberList.map((m) => m.id), [memberList]);

  // Fetch activities for all family members using person_data table
  const { items: rawActivities, loading, refetch } = useFamilyMembersData<ActivityData>(
    personIds,
    'activity'
  );

  // Group activities by data.id since multiple people can share the same activity
  interface GroupedActivity {
    id: string; // person_data id (first one)
    data_id: string; // shared data record id
    person_ids: string[]; // All people linked to this activity
    data: ActivityData;
  }

  const activities = useMemo(() => {
    const activityMap = new Map<string, GroupedActivity>();

    rawActivities.forEach((item: PersonDataItem<ActivityData>) => {
      const dataId = item.data?.id;
      if (!dataId) return;

      const existing = activityMap.get(dataId);
      if (existing) {
        // Add this person to the existing activity
        if (!existing.person_ids.includes(item.person_id)) {
          existing.person_ids.push(item.person_id);
        }
      } else {
        // Create new activity entry
        activityMap.set(dataId, {
          id: item.id,
          data_id: dataId,
          person_ids: [item.person_id],
          data: item.data?.data || {} as ActivityData,
        });
      }
    });

    return Array.from(activityMap.values());
  }, [rawActivities]);

  // Use shared data hook for inserting one data record linked to multiple people
  const { insertForMultiplePeople } = useSharedPersonData<ActivityData>('activity');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<any>(null);

  const handleSave = async (data: ActivityData, personIds: string[], _familyIds: string[]) => {
    try {
      if (selectedActivity?.id) {
        // When editing, update the data record directly
        await updateData(selectedActivity.data_id || selectedActivity.id, 'activity', data);
        enqueueSnackbar('Activity updated', { variant: 'success' });
      } else {
        // Insert one data record linked to all selected people
        await insertForMultiplePeople(data, personIds);
        enqueueSnackbar('Activity added', { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to save activity', { variant: 'error' });
      throw err;
    }
  };

  const handleDelete = async () => {
    if (!selectedActivity?.id) return;
    try {
      await deletePersonData(selectedActivity.id);
      enqueueSnackbar('Activity deleted', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to delete activity', { variant: 'error' });
      throw err;
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading activities..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Activities"
        subtitle="Manage sports, clubs, and extracurricular activities"
        actionLabel="Add Activity"
        actionIcon={AddIcon}
        onAction={() => {
          setSelectedActivity(null);
          setDialogOpen(true);
        }}
      />

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DataCard
            title="Activities"
            icon={ActivitiesIcon}
            iconColor={colors.activities}
            value={activities.length}
            onClick={() => {
              setSelectedActivity(null);
              setDialogOpen(true);
            }}
          />
        </Grid>

        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>All Activities</Typography>

              {activities.length === 0 ? (
                <EmptyState
                  icon={ActivitiesIcon}
                  title="No activities yet"
                  description="Add activities to track schedules and events"
                  actionLabel="Add Activity"
                  onAction={() => {
                    setSelectedActivity(null);
                    setDialogOpen(true);
                  }}
                />
              ) : (
                <Grid container spacing={2}>
                  {activities.map((activity: GroupedActivity) => {
                    const activityData = activity.data;

                    return (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={activity.data_id}>
                        <Card
                          variant="outlined"
                          sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                          onClick={() => {
                            setSelectedActivity({
                              id: activity.id,
                              data_id: activity.data_id,
                              person_ids: activity.person_ids,
                              data: activityData,
                            });
                            setDialogOpen(true);
                          }}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                              <Avatar sx={{ bgcolor: colors.activities }}>
                                <ActivitiesIcon />
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                  {activityData.name || 'Unnamed Activity'}
                                </Typography>
                                {activityData.type && (
                                  <Typography variant="caption" color="text.secondary">
                                    {activityData.type}
                                  </Typography>
                                )}
                              </Box>
                            </Box>

                            {/* Team, Coach, Cost */}
                            {(activityData.teamName || activityData.coach || activityData.cost) && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                {activityData.teamName && <Chip label={activityData.teamName} size="small" variant="outlined" />}
                                {activityData.coach && <Chip label={activityData.coach} size="small" variant="outlined" />}
                                {activityData.cost && <Chip label={`$${activityData.cost}`} size="small" color="primary" variant="outlined" />}
                              </Box>
                            )}

                            {/* Schedules */}
                            {Array.isArray(activityData.schedules) && activityData.schedules.length > 0 ? (
                              <Box sx={{ mb: 1 }}>
                                {activityData.schedules.map((sched: any, i: number) => (
                                  <Typography key={i} variant="body2" color="text.secondary">
                                    {[sched.scheduleType, sched.days?.join('/'), sched.startTime && `${sched.startTime}${sched.endTime ? `-${sched.endTime}` : ''}`, sched.location].filter(Boolean).join(' · ')}
                                  </Typography>
                                ))}
                              </Box>
                            ) : activityData.schedule ? (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {activityData.schedule}
                              </Typography>
                            ) : null}

                            {/* Show all people linked to this activity */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {(activity.person_ids || []).map((personId) => {
                                const personName = getMemberName(personId);
                                const personColor = getMemberColor(personId);
                                return (
                                  <Chip
                                    key={personId}
                                    label={personName}
                                    size="small"
                                    avatar={<Avatar sx={{ bgcolor: personColor }}>{personName.charAt(0)}</Avatar>}
                                  />
                                );
                              })}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <ActivityFormDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedActivity(null);
        }}
        onSave={handleSave}
        onDelete={selectedActivity ? handleDelete : undefined}
        activity={selectedActivity}
      />
    </Box>
  );
};

export default ActivitiesPage;
